import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'

/**
 * Minimal preset list of supported countries in the Add Property form.
 * The API accepts any 2-char ISO 3166-1 alpha-2 code, but v1 only exposes
 * the ones we actively support.
 */
export const SUPPORTED_COUNTRIES: { value: string; label: string }[] = [
  { value: 'CO', label: 'Colombia (CO)' },
  { value: 'US', label: 'United States (US)' },
  { value: 'MX', label: 'Mexico (MX)' },
]

export const propertyShape = {
  id: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable().optional(),
  bedroomCnt: z.number().nullable().optional(),
  bathroomCnt: z.number().nullable().optional(),
  owner: z.string().nullable().optional(),
  street: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  stateOrRegion: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  placeId: z.string().nullable().optional(),
  formattedAddress: z.string().nullable().optional().readonly(),
  calendarFeedUrl: z.string().nullable().optional().readonly(),
  // No .default(): zod v4 applies defaults inside .partial(), so every PATCH would reset these.
  inboundCalendarUrls: z.array(z.string().url()),
  // Per-property reporting config
  traEnabled: z.boolean(),
  traApiKey: z.string().optional(),
  traApiKeyMasked: z.string().nullable().optional().readonly(),
  // MINCIT RNT registration number — the property's own TRA registration.
  traRntCode: z.string().nullable().optional(),
  traEstablishmentName: z.string().nullable().optional(),
  traAccommodationType: z.string().nullable().optional(),
  sireEnabled: z.boolean(),
  // codigo_establecimiento issued by Migración Colombia's SIRE portal.
  sireEstablishmentCode: z.string().nullable().optional(),
  // Manual override for the SIRE city (DIVIPOLA) code.
  sireCityCode: z.string().nullable().optional(),
  // Resolved city code (override or accent-insensitive lookup), null if unresolvable.
  sireCityCodeResolved: z.string().nullable().optional().readonly(),
  // Human-readable city name matching the resolved code.
  sireCityNameResolved: z.string().nullable().optional().readonly(),
  reportsTra: z.boolean().readonly().optional(),
  reportsSire: z.boolean().readonly().optional(),
  timezone: z.string().nullable().optional(),
  // Default stay times as a local time-of-day ("HH:MM:SS"), never timezone-converted.
  checkInTime: z.string().optional(),
  checkOutTime: z.string().optional(),
}

export const propertyPriceInstanceShape = {
  id: z.string().readonly(),
  stayStart: z.string().or(z.date()).readonly(),
  stayEnd: z.string().or(z.date()).readonly(),
  weekPrice: z.number().readonly(),
  originalPrice: z.number().readonly(),
  discountPrice: z.number().readonly(),
  property: z.string().readonly(),
  propertyRef: z.object(propertyShape).readonly(),
  link: z.string().readonly(),
  isAvailable: z.boolean().readonly(),
  score: z.number().optional().nullable().readonly(),
  checkDate: z.string().or(z.date()).readonly(),
}

export const createPropertyShape = {
  name: z.string().min(1),
  country: z.string().length(2),
  owner: z.string().uuid(),
  description: z.string().nullable().optional(),
  street: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  stateOrRegion: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  placeId: z.string().nullable().optional(),
  bedroomCnt: z.number().int().nullable().optional(),
  bathroomCnt: z.number().int().nullable().optional(),
  personCnt: z.number().int().nullable().optional(),
}

export const propertyFilterShape = {
  organization: z.string(),
  // Server-side search across name / city / street — backed by DRF SearchFilter.
  search: z.string(),
}

export const propertyInstanceFilterShape = {
  property: z.string(),
  stayStart: z.string(),
  checkDate: z.string(),
  minBath: z.number(),
  maxBath: z.number(),
  availableOnly: z.boolean(),
  search: z.string(),
}

export type Property = GetInferredFromRaw<typeof propertyShape>
export type PropertyPriceInstance = GetInferredFromRaw<typeof propertyPriceInstanceShape>
export type PropertyFilter = Partial<GetInferredFromRaw<typeof propertyFilterShape>>
export type PropertyInstanceFilter = Partial<GetInferredFromRaw<typeof propertyInstanceFilterShape>>
export type CreateProperty = GetInferredFromRaw<typeof createPropertyShape>

export const sireCityShape = {
  code: z.string(),
  name: z.string(),
}
export type SireCity = GetInferredFromRaw<typeof sireCityShape>
